/**
 * 数据请求工具
 * 已经实现
 *   post方式
 * 待实现
 *   数据上报，错误上报
 */

import { formatTimes, randomn } from '@/utils/format';
import Vue from 'vue';
import md5 from 'js-md5';
import PromiseZ from '@/utils/promiseZ';
import serverCgiProxy from '@/utils/serverCgiProxy';
import serverCgiReport from '@/utils/serverCgiReport';
import { isBrowser, isServerRender } from '@/utils/env';
import cookies from '@/utils/cookies';
import config from '../config';
import humps from './humps';
import logger from './logger';

/**
 * 区分环境，测试环境
 */
const AMSHOST = config.cgi.amsHost;
const sAMSToken = 'b87eed2ed0afb7a9444b5c7a8855a0b0';

const checkParamsEqual = (a, b) => {
  const aProps = Object.getOwnPropertyNames(a);
  const bProps = Object.getOwnPropertyNames(b);
  if (aProps.length !== bProps.length) {
    return false;
  }
  const transferString = (val) => {
    if (['string', 'object'].indexOf(typeof val) > -1) {
      return val;
    }
    return `${val}`;
  };
  for (let i = 0; i < aProps.length; i++) {
    const propName = aProps[i];

    const propA = transferString(a[propName]);
    const propB = transferString(b[propName]);
    if ((typeof (propA) === 'object')) {
      if (propA === null || propB === null) {
        if (propA !== propB) {
          return false;
        }
      } else {
        const res = checkParamsEqual(propA, propB);
        if (!res) return false;
      }
    } else if (propA !== propB) {
      return false;
    }
  }
  return true;
};

/**
 * 获取防CSRF的Token
 * @param {String} accessToken
 * */
function getCsrfToken(accessToken) {
  // console.log('accessToken', accessToken);
  let hash = 5188;
  for (let i = 0, len = accessToken.length; i < len; ++i) {
    hash += (hash << 5) + accessToken.charCodeAt(i);
  }
  return String(hash & 0x7fffffff);
}

function guid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    // eslint-disable-next-line no-mixed-operators
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

async function getHttpHeader() {
  try {
    return await Vue.Jsbridge.getHttpHeader();
  } catch (e) {
    return {};
  }
}

const goRequest = async ({
  headerMap, path, params, options, resolve, reject, raw, customHeaders,
} = {}) => {
  if (!headerMap) {
    headerMap = await Vue.Jsbridge.getHttpHeader() || {};
  }
  let { host } = config.cgi;
  try {
    if (/^\/?knockknock\./.test(path)) {
      host = await config.getKKHost();
    } else {
      host = await config.getCGI();
    }
  } catch (error) {
    logger.error('getCGI Exception');
  }
  const token = getCsrfToken(headerMap && headerMap.access_token ? headerMap.access_token : '');
  const requestId = `${guid()}-${new Date().getTime()}`;
  const header = Vue.Native.Platform === 'browser'
    ? {
      'Content-Type': 'application/json',
      commReq: headerMap.commReq,
      Cookie: headerMap.cookie,
      'X-Odp-Destination-Service': 'community-group-manage-srv',
    }
    : {
      'Content-Type': 'application/json',
      commReq: headerMap.commReq,
      Cookie: headerMap.cookie,
      'User-Agent': headerMap.userAgent,
      'request-id': requestId,
      'X-Odp-Destination-Service': 'community-group-manage-srv',
    };
  // 开始打点
  // const timer = new TimePointer({ module: path });
  const rowParams = params;
  if (options && options.isHumps) { // 驼峰转下划线
    params = humps.decamelizeKeys(params);
  }
  let requestParams = {
    body: JSON.stringify(params),
    method: 'POST',
    credentials: 'include', // 允许携带cookie
    headers: { ...header, ...customHeaders, 'G-Token': token },
  };
  if (path !== 'GCReportSrv/GCReport' && path !== 'community.GCReportSrv/GCReport') { // 上报日志，不大本地
    logger.info(`Net Request, requestId: ${requestId}, mode: ${options && options.mode}, host: ${host}, path: ${path}, params: ${JSON.stringify(params)}, header: ${JSON.stringify(requestParams.headers)}`);
  }
  if (isServerRender) {
    requestParams.timeout = 3000;
  }
  const url = `${host}${path}`;
  requestParams = serverCgiProxy.request(url, requestParams); // 服务端请求走内网
  requestParams = serverCgiReport.request(url, requestParams); // 服务端请求上报
  fetch(url, requestParams)
    .then(async (res) => {
      // timer.setTime('responseTime');
      serverCgiProxy.response(url, requestParams, res); // 代理只做连通性上报，因此解析失败也认为成功
      try {
        let result = await res.json(res.body);
        serverCgiReport.response(url, requestParams, result);
        if (options && options.isHumps) { // 驼峰转下划线
          result = humps.camelizeKeys(result);
        }
        //  连请求头一起返回
        if (raw) {
          return { ...result, headers: res.headers };
        }
        return result;
      } catch (error) {
        authenticateFailed(error);
        logger.error('Response Parse Exception', path, res);
        serverCgiReport.error(url, requestParams, res);
        throw error;
      }
    })
    .then((response) => {
      if (path !== 'GCReportSrv/GCReport' && path !== 'community.GCReportSrv/GCReport') { // 上报日志，不大本地
        logger.debug(`Net Response, requestId: ${requestId}, mode: ${options && options.mode}, host: ${host}, path: ${path}, response: ${JSON.stringify(response)}`);
      }
      if (response && response.ret === 0) {
        if (options && options.mode !== 'normal') {
          // let {uuid, uid} = headerMap
          for (const i in rowParams) {
            if (rowParams[i] === undefined) rowParams[i] = 'undefined';
          }
          const value = JSON.stringify({
            params: rowParams,
            data: response,
            expire: Date.now() + 30 * 60 * 1000,
          });
          const dataObj = {
            key: path, value, user_related: 1, action: 3, level: 1,
          };
          Vue.Native.callNativeWithPromise('GameCommunityJsModule', 'curdData', JSON.stringify(dataObj));
        }
        resolve(response);
        // return response;
      } else {
        logger.warn(`Net Response Warn, requestId: ${requestId}, host: ${host}, path: ${path}, response: ${JSON.stringify(response)}`);
        throw response;
      }
      resolve(response);
    })
    .catch((error) => {
      authenticateFailed(error);
      logger.error(`Net Response Exception, host: ${host}, path: ${path}, params: ${JSON.stringify(params)}, error: ${error.message || error.toString()}`);
      reject(error);
      // throw error; // 会造成uncatched exception
    });
};

export const http = {
  post(path, params = {}, {
    mode, isHumps, host, raw, customHeaders,
  } = { mode: 'normal' }) {
    if (mode === 'cache' || mode === 'both') {
      const O = mode === 'both' ? PromiseZ : Promise;
      return new O((resolve, reject) => {
        getHttpHeader()
          .then((res) => {
            const headerMap = res;
            if (mode === 'both') {
              goRequest({
                headerMap,
                host,
                path,
                params,
                options: { mode, isHumps },
                resolve,
                reject,
              });
            }
            // let {uuid, uid} = res
            const dataObj = {
              key: path, user_related: 1, action: 1, level: 1,
            };
            Vue.Native.callNativeWithPromise('GameCommunityJsModule', 'curdData', JSON.stringify(dataObj))
              .then((res) => {
                console.log(`mode: ${mode}, path: ${path}, get cache successfully: `, res);
                try {
                  let json = res;
                  if (typeof res === 'string') {
                    json = JSON.parse(res);
                  }
                  const { expire } = json;
                  const now = Date.now();
                  const timeStatus = now < expire;
                  if (typeof json.params === 'string') {
                    json.params = JSON.parse(json.params);
                  }
                  const paramsStatus = checkParamsEqual(params, json.params);
                  if (path !== 'GCReportSrv/GCReport' && path !== 'community.GCReportSrv/GCReport') { // 上报日志，不大本地
                    logger.debug(`mode: ${mode}, path: ${path}, check: timeStatus: ${timeStatus}, paramsStatus: ${paramsStatus}`, {
                      expireTime: expire,
                      nowTime: now,
                      expireParams: json.params,
                      nowParams: params,
                    });
                  }
                  if (timeStatus && paramsStatus) {
                    // console.log(`mode: ${mode}, from cache ${path}`, json)
                    if (typeof json.data === 'string') {
                      json.data = JSON.parse(json.data);
                    }
                    if (path !== 'GCReportSrv/GCReport' && path !== 'community.GCReportSrv/GCReport') {
                      logger.debug(`mode: ${mode}, path: ${path}, from: cache`);
                    }
                    if (json.data.ret === 0) {
                      resolve(json.data);
                    } else {
                      throw new Error('');
                    }
                  } else {
                    throw new Error('');
                  }
                } catch (e) {
                  if (mode !== 'both') {
                    goRequest({
                      headerMap,
                      path,
                      params,
                      options: { mode, isHumps },
                      resolve,
                      reject,
                    });
                  }
                }
              });
          });
      });
    }
    return new Promise((resolve, reject) => {
      goRequest({
        path,
        params,
        options: { mode, isHumps },
        resolve,
        reject,
        raw,
        customHeaders,
      });
    });
  },

  async postAMS(apiName, req = {}) {
    const headerMap = await Vue.Jsbridge.getHttpHeader() || {};
    const token = getCsrfToken(headerMap && headerMap.access_token ? headerMap.access_token : '');
    const dateTime = Date.now();
    const timestamp = Math.floor(dateTime / 1000);
    let amsParameter = {
      cmd: 'jjsonrouter',
      // iAmsActivityId: '11648',
      openid: '%3CsAMSGameOpenId%3E',
      sAMSAppId: 'IEG-AMS-11648',
      sAMSVersion: '2.0',
      sServiceType: 'tgclub',
    };

    const time = formatTimes({ serverTimezone: true });
    amsParameter = setAMSParam(headerMap, amsParameter);
    amsParameter.sAMSTimestamp = timestamp;
    amsParameter.sCloudApiName = `ams.resource.${apiName}`;
    amsParameter.sAmsSerial = `AMS-TGCLUB-${time}-${randomn(6)}-11648-68125`;
    amsParameter.g_tk = token;
    let urlParams = paramsStrSort(amsParameter);
    const sAMSSign = sign(urlParams, sAMSToken);
    urlParams = `${urlParams}&sAMSSign=${sAMSSign}`;
    const request = {};
    request.cmd = apiName.replace(/([A-Z])/g, '_$1').toLowerCase();
    request.acct_type = amsParameter.sAMSAcctype;
    request.appid = headerMap.appid;
    request.business = 'community';
    request.device = (Vue.Native && Vue.Native.Platform === 'android') ? 'and' : 'ios';
    request.openid = '<sAMSGameOpenId>';
    request.access_token = headerMap.access_token;
    request.timestamp = timestamp;
    request.sign = sAMSSign;
    request.billno = amsParameter.sAmsSerial;
    request.req = JSON.stringify(req);
    const body = {};
    body.request = request;
    // console.log('****httpams request:', headerMap, amsParameter, request);
    // console.log(AMSHOST + '?' + urlParams)
    logger.info('AMS Request', headerMap, amsParameter, request, (`${AMSHOST}?${urlParams}`));
    const header = Vue.Native.Platform === 'browser' ? {
      'Content-Type': 'application/json',
      commReq: headerMap.commReq,
      Cookie: headerMap.cookie,
    } : {
      'Content-Type': 'application/json',
      commReq: headerMap.commReq,
      Cookie: headerMap.cookie,
      'User-Agent': headerMap.userAgent,
    };
    console.log('AMS Request', header);
    return fetch(`${AMSHOST}?${urlParams}`, {
      body: JSON.stringify(body),
      method: 'POST',
      credentials: 'same-origin', // 允许携带cookie
      headers: header,
    })
      .then((res) => {
        try {
          const temp = JSON.parse(res.body);
          if (temp.ret === 0 && temp.rsp && temp.rsp.response && temp.rsp.response.data) {
            return JSON.parse(temp.rsp.response.data);
          }
          logger.warn('AMS Response Warn', temp);
          throw temp;
        } catch (error) {
          logger.error('AMS Response Parse Exception', res);
          throw error;
        }
      })
      .then((response) => {
        logger.debug('AMS Response', response);
        if (response && response.ret === 0) {
          return response;
        }
        logger.error('AMS Response Error', response);
        throw response;
      })
      .catch((error) => {
        // logger.groupCollapsed('数据请求失败');
        // logger.groupEnd();
        logger.error('AMS Response Exception', error);
        throw error;
      });
  },
  /**
   * 合并请求
   * @param pipeline 请求数组
   * @param mode any 有一个成功就为成功 all 全部成功才算成功
   * @returns {Promise<* | undefined | void>}
   */
  async postMerge(pipeline, mode = 'all') {
    const headerMap = await Vue.Jsbridge.getHttpHeader() || {};
    const token = getCsrfToken(headerMap && headerMap.access_token ? headerMap.access_token : '');

    let { host } = config.cgi;
    try {
      host = await config.getCGI();
    } catch (error) {
      logger.error('getCGI Exception');
    }
    host += 'apisix/batch-requests';

    const body = {};
    body.headers = {};
    body.headers['Content-Type'] = 'application/json';
    body.headers['G-Token'] = token;
    body.headers.commReq = headerMap.commReq;
    body.pipeline = pipeline;
    body.headers['X-Odp-Destination-Service'] = 'community-group-manage-srv';

    logger.info('postMerge Request', headerMap, body, host);
    const header = Vue.Native.Platform === 'browser' ? {
      'Content-Type': 'text/plain',
      commReq: headerMap.commReq,
      Cookie: headerMap.cookie,
    } : {
      'Content-Type': 'text/plain',
      commReq: headerMap.commReq,
      Cookie: headerMap.cookie,
      'User-Agent': headerMap.userAgent,
    };
    return fetch(host, {
      body: JSON.stringify(body),
      method: 'POST',
      credentials: 'include', // 允许携带cookie
      headers: { ...header, 'G-Token': token },
    })
      .then(async (res) => {
        let rspBody = null;
        try {
          rspBody = await res.json(res.body);
        } catch (error) {
          logger.error('postMerge Response Parse Exception', pipeline, res);
          throw error;
        }
        const result = rspBody.map((item, index) => {
          try {
            return JSON.parse(item.body);
          } catch (err) {
            logger.error('postMerge Response Body Item Parse Exception', pipeline[index], item, err);
            return null;
          }
        }); // 生成数组
        logger.debug('postMerge Response', result);
        if ((mode === 'any' && result.some(item => !!item))
        || (mode === 'all' && result.every(item => !!item))) {
          return result;
        }
        throw new Error({
          message: `mode: ${mode}, result: ${JSON.stringify(result)}`,
        });
      })
      .catch((error) => {
        authenticateFailed(error);
        logger.error('postMerge Response Exception', error.message || error);
        throw error;
      });
  },

  fetch,
};
function sign(paramStr, sAMSToken) {
  const newUrl = `${paramStr}&sAMSToken=${sAMSToken}`;
  return md5(newUrl).toUpperCase();
}

function paramsStrSort(params) {
  const arr = [];
  for (const i in params) {
    if (Object.prototype.hasOwnProperty.call(params, i)) {
      arr.push((`${i}=${params[i]}`));
    }
  }
  const paramsStr = arr.join(('&'));
  const urlStr = paramsStr.split('&').sort()
    .join('&');
  return urlStr;
}

function setAMSParam(headerMap, amsParameter) {
  /* eslint-disable */
  if (headerMap.acct_type == '1') {
    amsParameter.sAMSAcctype = 'wx'
    amsParameter.sAMSTargetAppId = 'wxa09391bb0085f4b0'
  } else if (headerMap.acct_type == '2') {
    amsParameter.sAMSTargetAppId = '101484782'
    amsParameter.sAMSAcctype = 'qq'
    amsParameter.sAMSSourceAppId = headerMap.appid
  }
  amsParameter.sAMSAppOpenId = headerMap.openid
  amsParameter.sAMSAccessToken = headerMap.access_token
  return amsParameter
}

function authenticateFailed(data) {
  if (isBrowser() && !isServerRender) { //浏览器端
    try {
      const storageKey = '__clear_cookies';
      const nowTime = +(new Date());
      const lastClearTime = window.localStorage.getItem(storageKey) || 0;
      const diffLargeTime = 129600000; // 1.5 * 24 * 60 * 60 * 1000
      const authErrorMessage = [
        'no access_token in cookie',
        'no loginType or acctype',
        'no openid in cookie',
        'no appid in cookie'
      ];
      if ((data.ret === 7 && (data.message === 'authenticate failed' || data.message === 'authenticate op failed') || authErrorMessage.indexOf(data.message) !== -1 ) && (nowTime - lastClearTime >= diffLargeTime)) {
        window.localStorage.setItem(storageKey, nowTime);
        const clearCookieList = ['accessToken', 'access_token', 'appId', 'appid', 'openId', 'openid', 'loginType', 'acctype'];
        const domainList = window.location.hostname.replace(/(.*).qq\.com/ig, '$1.').split('.');
        const domain = 'qq.com';
        clearCookieList.forEach((key) => {
          let clearDomain = domain;
          for (let i = domainList.length + 1; i--;) {
            clearDomain = typeof domainList[i] !== 'undefined' ? `${domainList[i]}.${clearDomain}` : clearDomain;
            cookies.clear(key, { path: '/', domain: clearDomain });
            clearDomain = clearDomain.replace(/^\./ig, '');
          }
          cookies.clear(key, { path: '/' });
        });
        window.location.reload();
      }
    } catch (e) {
      // nothing to do
    }
  }

}


export default http
